import {
  lazy,
  memo,
  Suspense,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from 'react';
import { useLocation, useNavigate, useNavigationType, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { INBOX_PATH } from '../lib/routeConfig';
import {
  Building2,
  ChevronDown,
  Inbox as InboxIcon,
  Plus,
  Search,
  SlidersHorizontal,
  Tag,
  X,
} from 'lucide-react';
import { listInbox, type InboxRow, type InboxStatus, type InboxView, type InboxBranchOption } from '../api/inbox';
import { ApiError } from '../api/client';
import { subscribeInboxChanges } from '../api/inboxStream';
import type { SortOrder } from '../api/tickets';
import { listDepartments, type Department } from '../api/departments';
import { listAgents, type Agent } from '../api/agents';
import { listBranches, type Branch } from '../api/branches';
import { useAuth } from '../context/AuthContext';
import { CAPABILITIES } from '../lib/capabilities';
import { Spark } from '../components/Spark';
import { ListSkeleton } from '../components/Skeletons';
import { OutboundModal } from '../components/OutboundModal';
import { monogram } from '../lib/monogram';
import { useTemplateCatalog, type TemplateCatalogState } from '../lib/templateCatalog';
import { contactsPreview, templatePreview } from '../lib/templateDisplay';
import { mediaPreview } from '../lib/mediaPreview';
import { preferredCustomerName } from '../lib/customerName';
import { customerTagColor } from '../lib/customerTags';
import { useMyTags, withPersonalRowTags } from '../lib/personalTags';
import { useSnackbar } from '../components/Snackbar';

// Memoized: the detail pane (transcript included) re-renders only when ITS props
// change — not on every Inbox list refresh, keystroke in the search box or
// page append. Its callbacks below are stable for that reason.
const TicketDetailPane = lazy(async () => ({
  default: memo((await import('./TicketDetailPane')).TicketDetailPane),
}));
const ConversationDetailPane = lazy(async () => ({
  default: memo((await import('./ConversationDetail')).ConversationDetailPane),
}));

// `adminOnly` mirrors the server gate: the campaign lens returns no rows to a
// non-admin, so offering the option would be a tab that is always empty.
// Managers (admin/supervisor) get two unread lenses. "Unread" is assigned_unread:
// unread conversations ASSIGNED to the viewed person. "View as" is the full unread
// lens of that person's Inbox — for a supervisor that spans every agent's customers
// in their branches, which is why it is named after the "Viewing <name>'s Inbox"
// banner. A regular agent keeps the single "Unread" lens, unchanged.
const STATUS_OPTIONS: { key: InboxStatus; labelKey: string; adminOnly?: boolean; managerOnly?: boolean; managerLabelKey?: string }[] = [
  { key: 'all', labelKey: 'inbox.status_all' },
  { key: 'needs_you', labelKey: 'inbox.status_needs_you' },
  { key: 'assigned_unread', labelKey: 'inbox.status_unread', managerOnly: true },
  { key: 'unread', labelKey: 'inbox.status_unread', managerLabelKey: 'inbox.status_view_as' },
  { key: 'bot_replying', labelKey: 'inbox.status_bot_replying' },
  { key: 'paused', labelKey: 'inbox.status_paused' },
  { key: 'campaign', labelKey: 'inbox.status_campaign', adminOnly: true },
  { key: 'closed', labelKey: 'inbox.status_closed' },
];

// Accepts every status for URL parsing, including admin-only ones: a non-admin
// arriving at ?status=campaign is corrected by statusOptionsFor below rather
// than being dropped into an unparseable state.
function isInboxStatus(value: string | null): value is InboxStatus {
  return STATUS_OPTIONS.some((option) => option.key === value);
}

function statusOptionsFor(isAdmin: boolean, isManager: boolean): typeof STATUS_OPTIONS {
  return STATUS_OPTIONS.filter((option) => (isAdmin || !option.adminOnly) && (isManager || !option.managerOnly));
}

function statusLabelKey(option: (typeof STATUS_OPTIONS)[number], isManager: boolean): string {
  return isManager && option.managerLabelKey ? option.managerLabelKey : option.labelKey;
}

// The header count names what it counts for the three reply-state lenses
// (matching / unread awaiting a reply / awaiting a reply); other lenses keep the
// generic "N conversations".
function totalCountLabelKey(filter: InboxStatus): string {
  if (filter === 'all') return 'inbox.total_count_all';
  if (filter === 'unread' || filter === 'assigned_unread') return 'inbox.total_count_unread';
  if (filter === 'needs_you') return 'inbox.total_count_needs_you';
  return 'inbox.total_count';
}

function relativeTime(iso: string, lng: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  const isAr = lng === 'ar';
  if (mins < 1) return isAr ? 'دلوقتي' : 'just now';
  if (mins < 60) return isAr ? `من ${mins} دقيقة` : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return isAr ? `من ${hrs} ساعة` : `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return isAr ? `من ${days} يوم` : `${days}d ago`;
}

// Memoized with a stable onOpen: a row re-renders when its own data, selection
// or the template map changes — not for every list-level state change.
const InboxListRow = memo(function InboxListRow({ row, selected, templateCatalog, onOpen }: {
  row: InboxRow;
  selected: boolean;
  templateCatalog: TemplateCatalogState;
  onOpen: (row: InboxRow) => void;
}) {
  const { t, i18n } = useTranslation();
  const isConversation = row.source === 'conversation';
  const statusLabel = isConversation
    ? t(row.botPaused ? 'inbox.row_paused' : 'inbox.row_bot_replying')
    : row.status === 'closed' ? t('ticket.resolved_badge') : null;
  const expiry = row.status !== 'closed' && row.windowExpiresAt
    ? new Date(row.windowExpiresAt).getTime() - Date.now()
    : null;
  const displayName = preferredCustomerName(
    row.manualName ?? row.name,
    row.whatsappProfileName,
  );
  const title = displayName ?? row.phone;
  const assignedName = !isConversation && row.stickyAssignmentEnabled && !row.stickyAssignmentTimedOut && row.assignedAgent
    ? (row.assignedAgent.name ?? t('context.notes_unknown_author'))
    : null;
  // A legacy "[name] params" template row reads as its text (with evidence) or
  // "Template: name" — only for an agent's message (never a customer's or the
  // bot's text, never a media marker). New template sends store their text.
  // A received contact card reads as "Contact card", not its stored "[contacts]" marker.
  // A media row (by its structured type) reads as "Voice message", "Photo", … — never an internal marker such as
  // "[audio:error]" (a transcription failure; the recording itself plays in the conversation).
  const preview = mediaPreview(row.lastMessage, row.lastMessageType,
    { voice: row.lastMessageVoice, failed: row.lastMessageFailed }, t)
    ?? (row.lastMessageTyped ? null : templatePreview(row.lastMessage, templateCatalog, t, {
    fromAgent: row.lastSender === 'agent' ? true : row.lastSender === null ? null : false,
  }))
    ?? contactsPreview(row.lastMessage, t)
    ?? row.lastMessage
    ?? row.reason
    ?? '';

  return (
    <button
      data-ticket-row={row.ticketId ?? `phone:${row.phone}`}
      onClick={() => onOpen(row)}
      aria-current={selected}
      className={`group w-full rounded-2xl text-start flex items-center gap-3 px-3
        transition-[background-color,box-shadow] duration-200 motion-reduce:transition-none
        focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[-2px]
        ${selected
          ? 'bg-primary/[0.09] [box-shadow:inset_0_0_0_1px_rgb(var(--md-sys-color-primary)/0.18)]'
          : 'hover:bg-recessed/55'}`}
    >
      <span
        className={`shrink-0 flex size-11 items-center justify-center rounded-full text-xs font-semibold tabular-nums
          ${selected ? 'bg-surface text-primary' : 'bg-recessed text-secondary'}`}
        aria-hidden="true"
      >
        {monogram(displayName, row.phone)}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1 py-2.5">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold leading-5 text-primary" dir="auto">
            {title}
          </span>
          <span className="shrink-0 text-[0.6875rem] leading-4 text-muted tabular-nums">
            {relativeTime(row.lastActivity, i18n.language)}
          </span>
        </span>
        {title !== row.phone && (
          <span className="truncate text-[0.6875rem] leading-4 text-muted tabular-nums" dir="ltr">
            {row.phone}
          </span>
        )}

        <span className="flex items-center gap-2">
          <span className="flex-1 truncate text-start text-xs leading-5 text-secondary" dir="auto">
            {preview}
          </span>
          {row.lastSender === 'campaign' && (
            <span className="shrink-0 rounded-full bg-tint-warning px-2 py-0.5 text-[0.625rem] font-medium text-status-warning">
              {row.campaignDepartment
                ? t('inbox.campaign_badge_dept', { department: row.campaignDepartment.name })
                : t('inbox.campaign_badge')}
            </span>
          )}
          {!isConversation && row.customerBlocked && (
            <span className="shrink-0 rounded-full bg-tint-danger px-2 py-0.5 text-[0.625rem] font-medium text-status-danger">
              {t('inbox.blocked_badge')}
            </span>
          )}
          {statusLabel && (
            <span className="shrink-0 rounded-full bg-primary/[0.08] px-2 py-0.5 text-[0.625rem] font-medium text-primary">
              {statusLabel}
            </span>
          )}
          {expiry !== null && expiry <= 0 && (
            <span className="shrink-0 text-[0.625rem] font-medium text-status-danger">
              {t('inbox.window_closed_badge')}
            </span>
          )}
          {expiry !== null && expiry > 0 && expiry < 4 * 3600_000 && (
            <span className="shrink-0 text-[0.625rem] font-medium text-status-warning">
              {t('inbox.time_remaining', {
                time: expiry >= 3600_000
                  ? `${Math.floor(expiry / 3600_000)}h ${Math.floor((expiry % 3600_000) / 60_000)}m`
                  : `${Math.floor(expiry / 60_000)}m`,
              })}
            </span>
          )}
          {CAPABILITIES.unreadCounts && row.unreadCount > 0 && (
            <span
              className="flex h-[1.125rem] min-w-[1.125rem] shrink-0 items-center justify-center rounded-full
                bg-primary px-1 text-[0.625rem] font-semibold text-primary-foreground tabular-nums"
              aria-label={t('inbox.unread_count', { count: row.unreadCount })}
            >
              {row.unreadCount > 99 ? '99+' : row.unreadCount}
            </span>
          )}
        </span>

        {!isConversation && row.claim && (
          <span className="block truncate text-[0.6875rem] text-primary">
            {t('inbox.claiming', { name: row.claim.agentName })}
          </span>
        )}

        {(assignedName || row.branchName) && (
          <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
            {assignedName && (
              <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary/[0.08] px-2 py-0.5
                text-[0.625rem] text-primary">
                <span className="size-1.5 shrink-0 rounded-full bg-primary/70" aria-hidden="true" />
                <span className="truncate">{assignedName}</span>
              </span>
            )}
            {row.branchName && (
              <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-recessed px-2 py-0.5
                text-[0.625rem] text-muted">
                <Building2 size={10} className="shrink-0" aria-hidden="true" />
                <span className="truncate">{row.branchName}</span>
              </span>
            )}
          </span>
        )}

        {(row.tags?.length ?? 0) > 0 && (
          <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5">
            {(row.tags ?? []).slice(0, 2).map((tag) => (
              <span
                key={tag.id}
                className="inline-flex max-w-[9rem] items-center gap-1.5 rounded-full
                  border border-default/70 bg-surface px-2 py-0.5 text-[0.625rem] text-secondary"
              >
                <span
                  aria-hidden="true"
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: customerTagColor(tag.color) }}
                />
                <span className="truncate">{tag.name}</span>
              </span>
            ))}
            {(row.tags?.length ?? 0) > 2 && (
              <span className="text-[0.625rem] font-medium text-muted tabular-nums">
                +{(row.tags?.length ?? 0) - 2}
              </span>
            )}
          </span>
        )}
      </span>
    </button>
  );
});

function EmptyDetail() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
      <span className="flex size-16 items-center justify-center rounded-[1.4rem] bg-primary/10 text-primary">
        <InboxIcon size={28} strokeWidth={1.6} aria-hidden="true" />
      </span>
      <p className="mt-1 text-base font-semibold text-primary">{t('chat.select_conversation')}</p>
      <p className="max-w-xs text-sm leading-5 text-muted">{t('chat.select_conversation_hint')}</p>
    </div>
  );
}

function inboxPath(
  status: InboxStatus,
  conversation?: string,
  customerId?: string,
): string {
  const params = new URLSearchParams({ status });
  if (conversation) params.set('conversation', conversation);
  if (customerId) params.set('customer', customerId);
  return `${INBOX_PATH}?${params.toString()}`;
}

function ticketPath(ticketId: string, status: InboxStatus, customerId?: string): string {
  const params = new URLSearchParams({ status });
  if (customerId) params.set('customer', customerId);
  return `/tickets/${encodeURIComponent(ticketId)}?${params.toString()}`;
}

const PAGE_SIZE = 20;
const rowKey = (row: InboxRow): string => row.ticketId ?? `phone:${row.phone}`;

// ─── Live-refresh timings ────────────────────────────────────────────────────
// The list query is expensive (a nine-CTE join, run three times per request), so
// it is no longer re-read on a timer — the server watches a cheap watermark and
// pushes a change event instead. These three numbers are what keep that both
// responsive and honest.

/** Burst absorber. Several messages landing at once is one re-read, and the
 *  short delay also lets a just-sent reply be written before we go looking. */
const REFRESH_COALESCE_MS = 400;

/** Safety poll while the stream is healthy. Not redundancy — some row states
 *  change with the clock and not with a write (session-window expiry, bot-pause
 *  expiry, sticky-assignment timeout), so no watermark can ever notice them. */
const SAFETY_POLL_MS = 60_000;

/** Safety poll while the stream is NOT delivering. Roughly the old cadence, so
 *  a blocked stream degrades to the previous behaviour rather than to a frozen
 *  list — but only for the clients actually affected. */
const DEGRADED_POLL_MS = 15_000;

/** Ceiling on the single windowed refresh request (server caps this too, at the
 *  same value). Ten pages deep is already far more than anyone reads; past it the
 *  refresh covers the top of the list and leaves the tail untouched rather than
 *  growing the request without limit. */
const REFRESH_WINDOW_MAX = 200;

// Defensive dedupe: appends and the batched poll can, in rare reorder races,
// surface the same conversation twice. Keeping keys unique avoids duplicate
// React keys and double-rendered rows; first occurrence (highest page) wins.
function dedupeRows(rows: InboxRow[]): InboxRow[] {
  const seen = new Set<string>();
  const out: InboxRow[] = [];
  for (const row of rows) {
    const key = rowKey(row);
    if (!seen.has(key)) { seen.add(key); out.push(row); }
  }
  return out;
}

interface Reconciled {
  rows: InboxRow[];
  /** `true` means exactly this: an overlap with the previous list was found AND
   *  no cardinality hole was detected in the stitched result. It does NOT mean
   *  the tail beyond the refreshed window has been verified fresh — see the note
   *  on reconcileRefresh. It is the condition under which keeping a deep scroll
   *  position is defensible, not a freshness guarantee. */
  anchored: boolean;
}

/**
 * Folds a refreshed top-of-list window back into a list that was loaded deeper
 * than the window covers.
 *
 * Merging by POSITION is wrong, and wrong in a way that hides itself: it assumes
 * the fresh window still holds exactly the rows that occupied positions
 * 0..windowSize-1. One insertion at the top shifts everything down by one, so
 * the row that slid to position `windowSize` is in neither the fresh window nor
 * `prev.slice(windowSize)` — it silently disappears while the row COUNT stays
 * correct. A missing conversation is worse than a stale one.
 *
 * So the boundary is found by identity instead: the last row of the fresh window
 * is a candidate anchor, and whatever followed that same row before is a
 * candidate tail.
 *
 * The anchor is only a BEST-EFFORT overlap hint — finding it is not proof. If
 * the last fresh row is one that used to sit deep in the list, the rows between
 * the fresh window and that position belong to neither half:
 *
 *   previous  t-0 … t-319
 *   fresh     t-0 … t-198, t-250      (t-250 jumped into the window, at the end)
 *   anchor    t-250, at previous[250]
 *   tail      t-251 … t-319
 *   candidate 269 rows — t-199 … t-249 are simply gone
 *
 * So the candidate is additionally checked against the span it claims to cover.
 * Be precise about what that check is and is not:
 *
 *  • It is a CARDINALITY-HOLE GUARD, nothing more. It compares counts.
 *  • It catches the missing-row cases under test: an insertion at the top with a
 *    partial final page, a deep anchor leaving a gap behind it, and a reorder
 *    that pushes a loaded row below the span.
 *  • It does NOT prove the tail beyond REFRESH_WINDOW_MAX is current, or that it
 *    is still in the server's order. Those rows were not re-read.
 *  • Rows past the cap stay intentionally best-effort and possibly stale until
 *    the list is reloaded. That bound is the accepted cost of one request
 *    instead of N.
 *  • Equal-cardinality churn beyond the cap is therefore UNDETECTABLE here: if
 *    one row leaves the stale tail while another enters it, the count is
 *    unchanged and the guard passes over content that is already wrong.
 *
 * `anchored: true` accordingly means "overlap found and no cardinality hole
 * detected" — not "the entire tail is verified fresh".
 *
 * `loadedTarget` is how many rows the reader had actually loaded, capped by the
 * server's new total. It must come from the loaded PAGE CAPACITY
 * (pageRef.current * PAGE_SIZE), not from previous.length: when the deepest
 * loaded page was partial those differ, and using the row count silently
 * truncates the reconstructed list by the size of that partial page.
 */
function reconcileRefresh(
  previous: InboxRow[],
  fresh: InboxRow[],
  windowSize: number,
  loadedTarget: number,
): Reconciled {
  // The window reached at least as far as everything we had. The server's answer
  // is then the complete truth — including rows it dropped.
  if (previous.length <= windowSize || previous.length <= fresh.length) {
    return { rows: fresh, anchored: true };
  }

  const anchor = fresh[fresh.length - 1];
  const anchorKey = anchor ? rowKey(anchor) : null;
  const anchorAt = anchorKey === null
    ? -1
    : previous.findIndex((candidate) => rowKey(candidate) === anchorKey);

  if (anchorAt === -1) {
    // Nothing in the fresh window is a row we already had, so there is no way to
    // know where the old tail belongs — or whether those rows still match the
    // filter at all. Keeping them would be a guess presented as data. Show the
    // window the server actually vouched for and let the list shrink.
    return { rows: fresh, anchored: false };
  }

  // Everything after the anchor was outside the refreshed window and is carried
  // over as-is. dedupeRows keeps the FIRST occurrence, so a row that moved from
  // the tail into the fresh window keeps only its fresh copy.
  const tail = previous.slice(anchorAt + 1);
  // Hold the page depth the reader had scrolled to (already bounded by the
  // server's total by the caller), and never truncate the authoritative window.
  const target = Math.max(fresh.length, loadedTarget);
  const candidate = dedupeRows([...fresh, ...tail]);

  // Cardinality-hole guard. A candidate short of the target means the fresh
  // window and the carried tail do not meet — there is a gap between them, and
  // those rows would disappear without a trace while the list still looked
  // plausible. Fall back to the window the server actually vouched for rather
  // than publish a list with a hole in it. (This detects missing rows only; see
  // the note above on what it cannot detect.)
  if (candidate.length < target) {
    return { rows: fresh, anchored: false };
  }

  return { rows: candidate.slice(0, target), anchored: true };
}

// The Inbox view (search, filters, sort) is kept per browser tab and per signed-in agent, so RETURNING to the list —
// Back, a reload, or mobile Chrome restoring a discarded background tab (history navigation, "POP") — finds the same
// list. Opening the Inbox afresh (the menu, a link: "PUSH"/"REPLACE") starts with the default view, as before.
// sessionStorage only: it never outlives the tab and is never shared with another agent. Read defensively.
const SEARCH_MAX_LEN = 40;
const SORT_ORDERS: readonly SortOrder[] = ['newest', 'oldest', 'window_expiring', 'recently_resolved'];
interface SavedInboxView { sort: SortOrder; query: string; deptFilter: string; branchFilter: string; tagFilter: string; agentFilter: string }
function readSavedInboxView(key: string | null): Partial<SavedInboxView> {
  if (!key) return {};
  try {
    const v = JSON.parse(sessionStorage.getItem(key) ?? 'null') as Record<string, unknown> | null;
    if (!v || typeof v !== 'object') return {};
    const str = (x: unknown) => (typeof x === 'string' ? x.slice(0, 200) : undefined);
    const sort = SORT_ORDERS.find((o) => o === v['sort']);
    return { ...(sort ? { sort } : {}), query: str(v['query']), deptFilter: str(v['deptFilter']), branchFilter: str(v['branchFilter']),
      tagFilter: str(v['tagFilter']), agentFilter: str(v['agentFilter']) };
  } catch {
    return {};
  }
}

export default function Inbox() {
  const navigate = useNavigate();
  const { id: selectedId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { agent } = useAuth();
  const isAdmin = agent?.role === 'admin';
  const isManagerRole = isAdmin || agent?.role === 'supervisor';
  const requestedStatus = searchParams.get('status');
  const parsedStatus: InboxStatus = isInboxStatus(requestedStatus) ? requestedStatus : 'all';
  // A non-admin who lands on an admin-only lens (a shared link, a stale tab)
  // falls back to 'all' rather than sitting on a filter the select cannot show
  // and the server answers with nothing.
  const filter: InboxStatus = statusOptionsFor(isAdmin, isManagerRole).some((option) => option.key === parsedStatus)
    ? parsedStatus
    : 'all';
  const selectedPhone = selectedId ? null : searchParams.get('conversation');
  const routedCustomerId = searchParams.get('customer');

  const [showOutbound, setShowOutbound] = useState(false);
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [unreadFilterCount, setUnreadFilterCount] = useState(0);
  const [assignedUnreadFilterCount, setAssignedUnreadFilterCount] = useState(0);
  const [total, setTotal] = useState(0);
  // Which filter combination produced `total`. The header shows a count only when
  // it belongs to the CURRENT filters (viewer, status, branch, department, label,
  // search): while a new combination loads, or after it failed, the old number
  // would be a misleading count for a different result set. Sort is deliberately
  // not part of it — ordering never changes the total.
  const [totalKey, setTotalKey] = useState<string | null>(null);
  // Branch choices of the committed view (server: the effective viewer's
  // authorized branches, ∩ the manager's in a mirror). Null until the first
  // response; the caller's own branch list stands in until then.
  const [viewBranches, setViewBranches] = useState<InboxBranchOption[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const viewKey = agent?.id ? `inbox.view.v1:${agent.id}` : null;
  const navigationType = useNavigationType();
  const [savedView] = useState(() => (navigationType === 'POP' ? readSavedInboxView(viewKey) : {}));
  const [sort, setSort] = useState<SortOrder>(savedView.sort ?? 'newest');
  const [query, setQuery] = useState(savedView.query ?? '');
  const [debouncedQuery, setDebouncedQuery] = useState((savedView.query ?? '').trim().slice(0, SEARCH_MAX_LEN));
  const [deptFilter, setDeptFilter] = useState(savedView.deptFilter ?? '');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [branchFilter, setBranchFilter] = useState(savedView.branchFilter ?? '');
  const [branches, setBranches] = useState<Branch[]>([]);
  const [tagFilter, setTagFilter] = useState(savedView.tagFilter ?? '');
  // The signed-in account's OWN tags (personal; reset on every account change). "All my tags" is
  // only ever these — never another account's or the shared legacy tags.
  const myTags = useMyTags(agent?.id);
  const tags = myTags.tags;
  const { show: showSnackbar } = useSnackbar();
  // A tag filter that is not one of this account's tags (restored from this tab's saved view after
  // the tag went away, or answered 'not_found' by the server) is cleared — and the reader is told,
  // rather than silently shown a different result set.
  const clearForeignTagFilter = useCallback(() => {
    setTagFilter('');
    showSnackbar({ message: t('inbox.tag_filter_cleared') });
  }, [showSnackbar, t]);
  useEffect(() => {
    if (myTags.status === 'ready' && tagFilter && !tags.some((tag) => tag.id === tagFilter)) clearForeignTagFilter();
  }, [myTags.status, tags, tagFilter, clearForeignTagFilter]);
  // Another account in this mounted page (never a carried-over selection).
  const tagOwnerRef = useRef(agent?.id);
  useEffect(() => {
    if (tagOwnerRef.current !== agent?.id) setTagFilter('');
    tagOwnerRef.current = agent?.id;
  }, [agent?.id]);
  // Manager-only agent filter: '' = All Agents (team view), 'unassigned', or an
  // employee id — that employee's Inbox mirror (read-only; see `view`).
  const [agentFilter, setAgentFilter] = useState(savedView.agentFilter ?? '');
  useEffect(() => {
    if (!viewKey) return;
    try {
      sessionStorage.setItem(viewKey, JSON.stringify({ sort, query, deptFilter, branchFilter, tagFilter, agentFilter }));
    } catch { /* storage unavailable: the view simply is not restored */ }
  }, [viewKey, sort, query, deptFilter, branchFilter, tagFilter, agentFilter]);
  const [agents, setAgents] = useState<Agent[]>([]);
  // Whose Inbox the committed rows belong to, as the SERVER reported it.
  const [view, setView] = useState<InboxView | null>(null);
  // The server refused the requested employee mirror (unknown, inactive or
  // out-of-scope employee). Shown as such — never silently replaced by the
  // manager's own Inbox.
  const [mirrorRefused, setMirrorRefused] = useState(false);
  // The open conversation dropped out of the mirrored Inbox on a refresh (e.g.
  // a reply moved its owner). Explained instead of vanishing silently.
  const [rowLeftMirror, setRowLeftMirror] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const templateCatalog = useTemplateCatalog();
  const listRef = useRef<HTMLDivElement>(null);
  const isSupervisor = agent?.role === 'supervisor';
  const branchChoices: { id: string; name: string }[] = viewBranches ?? branches;
  // Shown whenever there is a real choice — and while a branch is selected, so
  // the reader can always return to All Branches.
  const showBranchFilter = branchChoices.length > 1 || branchFilter !== '';
  const showDepartmentFilter = (isAdmin || isSupervisor) && departments.length > 0;
  // A supervisor may inspect an agent's inbox exactly as the agent sees it, but
  // only for agents sharing one of their branches. That boundary is enforced
  // SERVER-side by GET /api/inbox, which returns 403 for any other target. The
  // client does no scoping of its own and must not be trusted to.
  const isManager = isAdmin || isSupervisor;
  const visibleFilterCount = 2
    + (showBranchFilter ? 1 : 0)
    + (showDepartmentFilter ? 1 : 0)
    + (isManager ? 1 : 0);
  // Below the two-pane breakpoint (phones, small tablets, landscape phones) the filter and sort controls fold behind
  // a "Filters" toggle: stacked, they took the whole height of a landscape phone and left the list no room at all.
  // At ≥ bp-lg they are always shown (CSS), exactly as before.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = (filter !== 'all' ? 1 : 0) + (branchFilter ? 1 : 0) + (deptFilter ? 1 : 0)
    + (tagFilter ? 1 : 0) + (agentFilter ? 1 : 0);
  // Infinite scroll bookkeeping. Refs (not state) so the scroll handler and the
  // interval poll read the live page/pages without being re-created on every
  // page change.
  const pageRef = useRef(1);
  const pagesRef = useRef(1);
  const loadingMoreRef = useRef(false);
  // Leading-edge throttle timestamp for the scroll handler.
  const lastScrollCheckRef = useRef(0);
  // When set, the next rows-commit restores this scrollTop. Only the quiet poll
  // sets it, so appends and fresh loads are unaffected.
  const preserveScrollRef = useRef<number | null>(null);
  // Mirrors `rows` so the refresh merge can read the current list without being
  // re-created on every change, and — more importantly — without doing the
  // reconciliation inside a setRows updater, which React may invoke twice and
  // which must therefore stay free of the ref/page bookkeeping the merge drives.
  const rowsRef = useRef<InboxRow[]>([]);
  // Live-refresh bookkeeping (see the live-refresh block further down).
  // `stale` records a change that arrived while the tab was hidden, so returning
  // to it catches up immediately instead of waiting for the next signal.
  const staleRef = useRef(false);
  const coalesceRef = useRef<number | null>(null);
  const [liveDegraded, setLiveDegraded] = useState(false);
  // One AbortController per query shape. A superseded query's requests are
  // cancelled, not merely ignored: they held browser connections (and server
  // database time) for a list nobody would see.
  const abortRef = useRef<AbortController>(new AbortController());
  // Single-flight for the quiet refresh, per query shape: a refresh asked for
  // while one (or a page append) is in flight runs ONCE afterwards instead of
  // overlapping — an overlapping refresh merged against a list that changed under
  // it and could drop a page that had just been appended.
  const refreshStateRef = useRef({ inFlight: false, again: false, loadBlocked: false });
  // Bumped per query shape so a superseded append cannot clear the current
  // query's in-flight flag.
  const generationRef = useRef(0);
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query.trim().slice(0, SEARCH_MAX_LEN)), 350);
    return () => clearTimeout(timeout);
  }, [query]);

  const countKey = JSON.stringify([filter, deptFilter, branchFilter, debouncedQuery, agentFilter, tagFilter]);
  const countKeyRef = useRef(countKey);
  useLayoutEffect(() => { countKeyRef.current = countKey; }, [countKey]);

  const sortOptions: { value: SortOrder; label: string }[] =
    filter === 'closed'
      ? [
          { value: 'recently_resolved', label: t('inbox.sort_recently_resolved') },
          { value: 'oldest', label: t('inbox.sort_oldest') },
        ]
      : filter === 'needs_you'
        ? [
            { value: 'newest', label: t('inbox.sort_newest') },
            { value: 'oldest', label: t('inbox.sort_oldest') },
            { value: 'window_expiring', label: t('inbox.sort_window') },
          ]
        : [
            { value: 'newest', label: t('inbox.sort_newest') },
            { value: 'oldest', label: t('inbox.sort_oldest') },
          ];

  const loadTags = myTags.reload;

  useEffect(() => {
    listDepartments().then((res) => setDepartments(res.departments)).catch(() => {});
    listBranches().then((res) => setBranches(res.branches)).catch(() => {});
  }, []);

  // Agents list for the manager "All Agents" filter. Fetched only for admins and
  // supervisors — /api/agents is requireAuth, not admin-guarded.
  //
  // For a branch-RESTRICTED supervisor that endpoint already returns only agents
  // sharing one of their branches, so the dropdown matches what the filter will
  // accept. The one case where they diverge is a supervisor with NO branches
  // assigned: /api/agents treats zero rows as unrestricted and lists everyone,
  // while /api/inbox deliberately fails closed and refuses every target. Such a
  // supervisor sees names in the dropdown and a plain error on selection. That
  // is the safe direction to be wrong in, and it self-resolves the moment they
  // are given a branch; widening /api/agents instead would change an endpoint
  // several other pages depend on.
  useEffect(() => {
    if (!isManager) return;
    listAgents().then((res) => setAgents(res.agents)).catch(() => {});
  }, [isManager]);

  // One call site, not three. The previous tagFilter/agentFilter branches passed
  // the same arguments and differed only in which TRAILING optionals they
  // included — and listInbox only sets a query param when its value is truthy,
  // so `undefined` and "omitted" are identical. Collapsing them is what makes
  // threading `windowSize` a one-line change instead of a three-place edit where
  // one branch could silently miss it.
  // The signed-in account is part of the query identity: a response requested for a previous
  // account never commits (the stale-response guard below compares this identity). Row tags are
  // kept only from a personal-tag server (withPersonalRowTags).
  const signedInId = agent?.id;
  const pageQuery = useCallback(
    async (page: number, windowSize?: number) => withPersonalRowTags(await listInbox(
      filter,
      page,
      PAGE_SIZE,
      sort,
      deptFilter || undefined,
      branchFilter || undefined,
      debouncedQuery || undefined,
      agentFilter || undefined,
      tagFilter || undefined,
      windowSize,
      abortRef.current.signal,
    )),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- signedInId: identity only (see above)
    [filter, sort, deptFilter, branchFilter, debouncedQuery, agentFilter, tagFilter, signedInId],
  );

  // Stale-response guard. `pageQuery` changes identity exactly when the query
  // shape does (lens, sort, filters, search, viewed employee), so a response is
  // current only if the query that produced it is still the active one. Without
  // this, switching from one employee's mirror to another could commit the
  // first employee's late-arriving rows, counts and read state under the
  // second's banner.
  const activeQueryRef = useRef(pageQuery);
  useLayoutEffect(() => {
    activeQueryRef.current = pageQuery;
    // A new query shape supersedes everything still in flight for the old one,
    // and a refresh scheduled for it (the reset below re-reads page 1 anyway).
    abortRef.current.abort();
    abortRef.current = new AbortController();
    if (coalesceRef.current !== null) window.clearTimeout(coalesceRef.current);
    coalesceRef.current = null;
    refreshStateRef.current = { inFlight: false, again: false, loadBlocked: false };
    generationRef.current += 1;
    loadingMoreRef.current = false;
  }, [pageQuery]);
  // The open row, for the refresh to notice it leaving a mirror.
  const selectionRef = useRef<{ id: string | undefined; phone: string | null }>({ id: selectedId, phone: selectedPhone });
  useLayoutEffect(() => { selectionRef.current = { id: selectedId, phone: selectedPhone }; }, [selectedId, selectedPhone]);
  useEffect(() => { setRowLeftMirror(false); }, [selectedId, selectedPhone, agentFilter]);

  // Fresh load / reset: fetch page 1 and replace the list. Runs on mount and
  // whenever filter, sort, department, branch, or the debounced search changes.
  const fetchReset = useCallback(async () => {
    // The request this call makes can be superseded (aborted) by a newer one of the same query — React re-running
    // the effect, a remount — and that newer request owns the loading/error state. An aborted request is never shown
    // as "failed to load" (it used to replace the loading skeleton with an error until the newer request finished).
    const signal = abortRef.current.signal;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    setMirrorRefused(false);
    try {
      const res = await pageQuery(1);
      if (activeQueryRef.current !== pageQuery) return;
      if (res.tagFilter === 'not_found') { clearForeignTagFilter(); return; }
      setRows(res.rows);
      pageRef.current = 1;
      pagesRef.current = res.pages;
      setTotal(res.total);
      setTotalKey(countKeyRef.current);
      setViewBranches(res.branchOptions ?? null);
      setUnreadFilterCount(res.counts.unread);
      setAssignedUnreadFilterCount(res.counts.assignedUnread ?? 0);
      setView(res.view ?? null);
      setError(null);
    } catch (err) {
      if (activeQueryRef.current !== pageQuery || signal.aborted) return;
      setView(null);
      setViewBranches(null);
      const refused = err instanceof ApiError
        && (err.errorCode === 'agent_not_found' || err.errorCode === 'agent_out_of_branch_scope');
      setMirrorRefused(refused);
      setError(t(refused ? 'inbox.mirror_refused' : 'inbox.error_load'));
    } finally {
      if (activeQueryRef.current === pageQuery && !signal.aborted) setLoading(false);
    }
  }, [pageQuery, t, clearForeignTagFilter]);

  // Append the next page. Guarded so concurrent scroll events (or a scroll that
  // fires while a fetch is in flight) cannot double-load, and stops once the
  // last page is reached.
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current) return;
    if (pageRef.current >= pagesRef.current) return;
    // Never alongside a quiet refresh (see refreshStateRef): the scroll check
    // runs again when it finishes.
    if (refreshStateRef.current.inFlight) { refreshStateRef.current.loadBlocked = true; return; }
    const generation = generationRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const next = pageRef.current + 1;
    try {
      const res = await pageQuery(next);
      // A page of a query that is no longer active belongs to another list.
      if (activeQueryRef.current !== pageQuery) return;
      setRows((prev) => dedupeRows([...prev, ...res.rows]));
      pageRef.current = next;
      pagesRef.current = res.pages;
      setTotal(res.total);
      setTotalKey(countKeyRef.current);
      setUnreadFilterCount(res.counts.unread);
      setAssignedUnreadFilterCount(res.counts.assignedUnread ?? 0);
    } catch {
      // Leave the loaded pages intact; the observer retries on the next scroll.
    } finally {
      if (generationRef.current === generation) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
        // A refresh requested while this page was loading runs now.
        if (refreshStateRef.current.again) void runRefreshRef.current();
      }
    }
  }, [pageQuery]);

  // Quiet refresh. Re-reads the whole loaded span in ONE bounded request and
  // replaces the accumulated set atomically.
  //
  // This used to issue one request per loaded page — three pages deep meant three
  // HTTP requests and three executions of the list query for a single change
  // notification. One windowed read gets the same rows, already consistent with
  // itself (a row that floated to the top cannot also linger at its old position,
  // because it all comes from one snapshot rather than three).
  //
  // Scroll position is pinned across the swap so the reader is not yanked to the
  // top, and dedupe stays as a belt-and-braces guard.
  const quietPoll = useCallback(async () => {
    const previous = rowsRef.current;
    // Bounded: a reader who has scrolled past the cap refreshes the top
    // REFRESH_WINDOW_MAX rows and reconciles the rest, rather than issuing an
    // ever-growing request. The server caps this independently too.
    const windowSize = Math.min(pageRef.current * PAGE_SIZE, REFRESH_WINDOW_MAX);
    try {
      const res = await pageQuery(1, windowSize);
      if (activeQueryRef.current !== pageQuery) return;
      // How many rows the reader had loaded, as PAGE CAPACITY rather than row
      // count. When the deepest loaded page was partial (315 rows over 16 pages)
      // those differ, and previous.length under-counts by the size of that
      // partial page — the merge would then drop exactly that many rows off the
      // end, unrecoverably, because pageRef would already equal `pages` and
      // loadMore refuses to run. Bounded by the server's total so the list never
      // claims more rows than exist.
      const loadedTarget = Math.min(pageRef.current * PAGE_SIZE, res.total);
      const { rows: merged, anchored } = reconcileRefresh(previous, res.rows, windowSize, loadedTarget);

      pagesRef.current = res.pages;
      // Keep the loaded-page counter consistent with what is actually on screen.
      // Without this, a fallback that shrank the list to 200 rows would still
      // believe 16 pages were loaded and ask for page 17 on the next scroll,
      // skipping everything in between.
      pageRef.current = Math.max(1, Math.ceil(merged.length / PAGE_SIZE));
      // Only pin the scroll when the merge was anchored. In the fallback case the
      // list legitimately shrank, and holding a deep offset would park the reader
      // past the end; correct data matters more than a preserved position.
      if (anchored) preserveScrollRef.current = listRef.current?.scrollTop ?? null;

      // In a mirror, an open conversation that was in the list and is not any
      // more has left the employee's Inbox (a reply or reassignment moved it).
      const nextView = res.view ?? null;
      if (nextView?.mode === 'employee') {
        const { id: openId, phone: openPhone } = selectionRef.current;
        const isOpen = (row: InboxRow) => (openId ? row.ticketId === openId : !row.ticketId && row.phone === openPhone);
        if ((openId || openPhone) && previous.some(isOpen) && !merged.some(isOpen)) setRowLeftMirror(true);
      }

      setRows(merged);
      setTotal(res.total);
      setTotalKey(countKeyRef.current);
      setViewBranches(res.branchOptions ?? null);
      setUnreadFilterCount(res.counts.unread);
      setAssignedUnreadFilterCount(res.counts.assignedUnread ?? 0);
      setView(nextView);
    } catch {
      // Ignore transient failures; the next change event or safety poll retries.
    }
  }, [pageQuery]);

  // The single funnel for running a quiet refresh (see refreshStateRef).
  const runRefresh = useCallback(async () => {
    const state = refreshStateRef.current;
    if (state.inFlight || loadingMoreRef.current) { state.again = true; return; }
    state.inFlight = true;
    state.again = false; // this run serves every request made so far
    try {
      await quietPoll();
    } finally {
      state.inFlight = false;
      if (refreshStateRef.current === state) {
        if (state.again) { state.again = false; void runRefreshRef.current(); }
        else if (state.loadBlocked) { state.loadBlocked = false; checkNearBottomRef.current(); }
      }
    }
  }, [quietPoll]);
  const runRefreshRef = useRef(runRefresh);
  useLayoutEffect(() => { runRefreshRef.current = runRefresh; }, [runRefresh]);
  // Assigned below, once the scroll check exists.
  const checkNearBottomRef = useRef<() => void>(() => {});

  // Reset to page 1 whenever the query shape changes.
  useEffect(() => { void fetchReset(); }, [fetchReset]);

  // ─── Live refresh ──────────────────────────────────────────────────────────
  // Previously a flat setInterval(5s). That re-ran the list query — every loaded
  // page of it, three times server-side each — for every open client regardless
  // of whether anything had changed or anyone was even looking, which is what
  // saturated Postgres once the whole team opened the app.
  //
  // Now the server pushes a change event (see api/inboxStream.ts) and the
  // re-read happens then. `requestRefresh` is the single funnel for every
  // reason to re-read, so all of the guards live in one place: coalesce bursts,
  // never re-read while the tab is hidden, and remember to catch up on return.
  const requestRefresh = useCallback(() => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      staleRef.current = true;
      return;
    }
    if (coalesceRef.current !== null) return;
    coalesceRef.current = window.setTimeout(() => {
      coalesceRef.current = null;
      void runRefresh();
    }, REFRESH_COALESCE_MS);
  }, [runRefresh]);

  // Held in a ref so the stream below can survive a filter change. Subscribing
  // through `requestRefresh` directly would tear the EventSource down and
  // rebuild it every time the query shape changed.
  const refreshRef = useRef(requestRefresh);
  useLayoutEffect(() => { refreshRef.current = requestRefresh; }, [requestRefresh]);

  useEffect(() => {
    const refresh = () => refreshRef.current();

    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || !staleRef.current) return;
      staleRef.current = false;
      refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const stop = subscribeInboxChanges({ onChange: refresh, onDegraded: setLiveDegraded });

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
      if (coalesceRef.current !== null) window.clearTimeout(coalesceRef.current);
      coalesceRef.current = null;
    };
  }, []);

  // Safety poll — slow while the stream is live, near the old cadence when it
  // is not. Separate from the subscription so switching between the two rates
  // never reconnects the stream.
  useEffect(() => {
    const interval = window.setInterval(
      () => refreshRef.current(),
      liveDegraded ? DEGRADED_POLL_MS : SAFETY_POLL_MS,
    );
    return () => window.clearInterval(interval);
  }, [liveDegraded]);

  // Restore scroll position after a quiet-poll swap (synchronously, before
  // paint) so an atomic list replacement never jumps the reader to the top.
  // Also keeps rowsRef in step with the committed list, which the refresh merge
  // reads as its "previous" state.
  useLayoutEffect(() => {
    rowsRef.current = rows;
    if (preserveScrollRef.current !== null && listRef.current) {
      listRef.current.scrollTop = preserveScrollRef.current;
    }
    preserveScrollRef.current = null;
  }, [rows]);

  // Auto-load on scroll — a direct scroll-position listener on the actual
  // scroll container (the overflow-y-auto div below). This replaces an
  // IntersectionObserver approach that never fired in production: less elegant,
  // far harder to get wrong, and it reads live values (scrollTop/clientHeight/
  // scrollHeight + the page refs) that we can also drive from a real scroll
  // event in tests. Leading-edge throttle keeps it off the per-pixel hot path,
  // with one trailing check so the resting position of a fast scroll is never
  // dropped; the loadingMore + page<pages guards prevent over-fetching
  // regardless. The next page is requested a full viewport (at least 600px)
  // before the end, so it is usually there before the reader is.
  const SCROLL_THRESHOLD_PX = 600;
  const SCROLL_THROTTLE_MS = 150;
  const trailingScrollRef = useRef<number | null>(null);
  const checkNearBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    if (loadingMoreRef.current) return;
    if (pageRef.current >= pagesRef.current) return;
    const threshold = Math.max(SCROLL_THRESHOLD_PX, el.clientHeight);
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (nearBottom) void loadMore();
  }, [loadMore]);
  useLayoutEffect(() => { checkNearBottomRef.current = checkNearBottom; }, [checkNearBottom]);
  const onListScroll = useCallback(() => {
    const wait = SCROLL_THROTTLE_MS - (Date.now() - lastScrollCheckRef.current);
    if (wait > 0) {
      if (trailingScrollRef.current === null) {
        trailingScrollRef.current = window.setTimeout(() => {
          trailingScrollRef.current = null;
          lastScrollCheckRef.current = Date.now();
          checkNearBottom();
        }, wait);
      }
      return;
    }
    lastScrollCheckRef.current = Date.now();
    checkNearBottom();
  }, [checkNearBottom]);
  useEffect(() => () => {
    if (trailingScrollRef.current !== null) window.clearTimeout(trailingScrollRef.current);
  }, []);
  // A list that does not yet fill its viewport cannot be scrolled, so no scroll
  // event would ever ask for the next page: check after every commit. (Only with
  // real layout — a zero-height container has nothing to fill.)
  useEffect(() => {
    const el = listRef.current;
    if (el && el.clientHeight > 0 && el.scrollHeight <= el.clientHeight) checkNearBottom();
  }, [rows, checkNearBottom]);

  function changeFilter(next: InboxStatus) {
    setSort(next === 'closed' ? 'recently_resolved' : 'newest');
    navigate(inboxPath(next));
  }

  // Opening a row from the list marks the new history entry, so the detail's Back returns to that list entry
  // (history -1) instead of pushing a new one: browser Back afterwards then leaves the Inbox as expected rather than
  // reopening the conversation just closed. A detail reached any other way (a link, a notification, a reload with no
  // list behind it) replaces itself with the list.
  const location = useLocation();
  const openedFromList = Boolean((location.state as { fromInboxList?: boolean } | null)?.fromInboxList);
  const openRow = useCallback((row: InboxRow) => {
    if (row.ticketId) {
      navigate(ticketPath(row.ticketId, filter, row.customerId), { state: { fromInboxList: true } });
    } else {
      navigate(inboxPath(filter, row.phone, row.customerId), { state: { fromInboxList: true } });
    }
  }, [navigate, filter]);
  // Stable detail-pane callbacks (the panes are memoized).
  const closeDetail = useCallback(() => {
    if (openedFromList) navigate(-1);
    else navigate(inboxPath(filter), { replace: true });
  }, [navigate, filter, openedFromList]);
  const onTicketStateChanged = useCallback(() => {
    void runRefresh();
    loadTags();
  }, [runRefresh, loadTags]);
  const onConversationStateChanged = useCallback(() => { void runRefresh(); }, [runRefresh]);

  function onListKeyDown(event: React.KeyboardEvent) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const rowButtons = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[data-ticket-row]') ?? []);
    if (rowButtons.length === 0) return;
    event.preventDefault();
    const current = rowButtons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'ArrowDown' ? Math.min(rowButtons.length - 1, current + 1) : Math.max(0, current - 1);
    rowButtons[next < 0 ? 0 : next]?.focus();
  }

  const emptyTitle = debouncedQuery
    ? t('inbox.empty_search')
    : filter === 'closed' ? t('inbox.empty_resolved_title')
    : filter === 'all' ? t('inbox.empty_all_title')
    : t('inbox.empty_title');
  const hasSelection = Boolean(selectedId || selectedPhone);
  const selectedRow = rows.find((row) =>
    selectedId ? row.ticketId === selectedId : !row.ticketId && row.phone === selectedPhone,
  );
  const selectedCustomerId = routedCustomerId ?? selectedRow?.customerId ?? null;
  // The mirror banner shows only once the SERVER confirms it is serving the
  // selected employee's Inbox — never for a request still in flight or refused.
  // The header count (exact distinct conversations matching EVERY current filter):
  //   ready        the committed total belongs to these filters;
  //   unavailable  the load for these filters failed — no number at all;
  //   loading      these filters (or a search still settling) have no total yet.
  const searchSettling = query.trim().slice(0, SEARCH_MAX_LEN) !== debouncedQuery;
  const countState: 'ready' | 'loading' | 'unavailable' =
    error !== null && !loading ? 'unavailable'
    : totalKey === countKey && !searchSettling ? 'ready'
    : 'loading';
  const mirror = isManager && view?.mode === 'employee' && view.employee.id === agentFilter ? view : null;
  const mirrorName = mirror
    ? mirror.employee.name ?? agents.find((a) => a.id === mirror.employee.id)?.name ?? t('context.notes_unknown_author')
    : '';
  // Changing whose Inbox is shown starts from All Branches: the branch choices
  // belong to the viewer, so a branch picked for one view may not exist in the
  // next. One update, so it costs one request, not two.
  const selectViewedAgent = (id: string) => {
    setAgentFilter(id);
    setBranchFilter('');
  };
  const exitMirror = () => selectViewedAgent('');
  // A committed view whose branch choices no longer include the selected branch
  // (e.g. the viewer's branches changed) falls back to All Branches.
  useEffect(() => {
    if (branchFilter && viewBranches && !viewBranches.some((choice) => choice.id === branchFilter)) setBranchFilter('');
  }, [viewBranches, branchFilter]);
  const selectedBlocked = selectedRow?.customerBlocked ?? false;
  const openTicketFromConversation = useCallback(
    (ticketId: string) => navigate(ticketPath(ticketId, filter, selectedCustomerId ?? undefined)),
    [navigate, filter, selectedCustomerId],
  );

  const listPane = (
    <section
      className={`${hasSelection ? 'hidden bp-lg:flex' : 'flex'} min-h-0 w-full flex-col bg-surface
        bp-lg:w-[410px] bp-lg:shrink-0 bp-lg:border-e bp-lg:border-default/80 xl:w-[430px]`}
      aria-label={t('inbox.title')}
    >
      {/* Short landscape phones (≤ 420px tall): the title row steps aside and the padding tightens, so the list keeps
          a usable height; search, filters and the mirror banner stay. */}
      <div className="flex shrink-0 flex-col gap-3 border-b border-default/80 px-4 pb-3 pt-4
        [@media(max-height:420px)]:gap-2 [@media(max-height:420px)]:pb-2 [@media(max-height:420px)]:pt-2">
        {/* Wraps only when the count does not fit beside the title (e.g. English at 320px), never overlapping it. */}
        <div data-testid="inbox-header" className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 [@media(max-height:420px)]:hidden">
          <div className="flex min-w-0 flex-1 basis-36 items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/[0.09] text-primary">
              <InboxIcon size={20} strokeWidth={1.8} aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block text-lg font-semibold leading-6 tracking-[-0.015em] text-primary">
                {t('inbox.title')}
              </span>
              <span className="block truncate text-xs leading-5 text-muted [@media(max-height:560px)]:hidden">
                {t('inbox.subtitle')}
              </span>
            </span>
          </div>
          <span
            data-testid="inbox-total-count"
            data-count-state={countState}
            aria-live="polite"
            aria-busy={countState === 'loading'}
            className="inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-full border
              border-outline-variant/60 bg-recessed px-3 text-label-small font-semibold text-secondary
              tabular-nums shadow-[0_2px_8px_rgb(var(--md-sys-color-shadow)/0.08)]"
          >
            {countState === 'ready'
              ? t(totalCountLabelKey(filter), { count: total })
              : t(countState === 'unavailable' ? 'inbox.total_count_unavailable' : 'inbox.total_count_loading')}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search
              size={17}
              aria-hidden="true"
              className="pointer-events-none absolute start-3.5 top-1/2 -translate-y-1/2 text-muted"
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              maxLength={SEARCH_MAX_LEN}
              placeholder={t('inbox.search_placeholder')}
              aria-label={t('inbox.search_placeholder')}
              // The app's own clear button replaces the browser's native one (no duplicate ×).
              className="h-11 w-full appearance-none rounded-full border border-default bg-surface ps-10 pe-12
                [&::-webkit-search-cancel-button]:hidden
                text-sm text-primary placeholder:text-muted transition-[border-color,box-shadow,background-color]
                focus:border-primary/45 focus:bg-surface-hover focus:outline-none
                focus:[box-shadow:0_0_0_3px_rgb(var(--md-sys-color-primary)/0.08)]"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label={t('inbox.clear_search')}
                className="absolute end-0 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center
                  rounded-full text-muted transition-colors hover:bg-default/70 hover:text-primary
                  focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-1"
              >
                <X size={15} aria-hidden="true" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowOutbound(true)}
            aria-label={t('outbound.button')}
            title={t('outbound.button')}
            className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground
              shadow-[0_6px_18px_rgb(var(--md-sys-color-primary)/0.18)]
              transition-[transform,box-shadow] hover:-translate-y-0.5
              hover:shadow-[0_8px_22px_rgb(var(--md-sys-color-primary)/0.24)]
              focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2
              motion-reduce:transform-none motion-reduce:transition-none"
          >
            <Plus size={20} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls="inbox-filter-panel"
            aria-label={activeFilterCount > 0
              ? t('inbox.filters_toggle_active', { count: activeFilterCount })
              : t('inbox.filters_toggle')}
            data-testid="inbox-filters-toggle"
            className={`relative flex size-11 shrink-0 items-center justify-center rounded-full border transition-colors bp-lg:hidden
              focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2
              ${filtersOpen ? 'border-primary/45 bg-primary/[0.09] text-primary' : 'border-default bg-surface text-secondary hover:bg-recessed'}`}
          >
            <SlidersHorizontal size={18} aria-hidden="true" />
            {activeFilterCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-1 end-0 flex min-w-5 h-5 items-center justify-center rounded-full bg-primary px-1
                  text-[11px] font-semibold leading-none text-primary-foreground tabular-nums"
              >
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {mirror && (
          <div
            data-testid="inbox-mirror-banner"
            role="status"
            className="flex items-start justify-between gap-3 rounded-2xl border border-primary/25 bg-primary/[0.055] px-3 py-2"
          >
            <span className="min-w-0 text-xs leading-5">
              <span className="block font-semibold text-primary" dir="auto">{t('inbox.mirror_viewing', { name: mirrorName })}</span>
              <span className="block text-secondary" dir="auto">{t('inbox.mirror_read_state', { name: mirrorName })}</span>
              {!mirror.exact && (
                <span data-testid="inbox-mirror-partial" className="block text-status-warning" dir="auto">
                  {t('inbox.mirror_partial', { name: mirrorName })}
                </span>
              )}
              {rowLeftMirror && (
                <span data-testid="inbox-mirror-row-left" className="block text-secondary" dir="auto">
                  {t('inbox.mirror_row_left', { name: mirrorName })}
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={exitMirror}
              aria-label={t('inbox.mirror_exit_label', { name: mirrorName })}
              className="inline-flex h-11 bp-lg:h-8 shrink-0 items-center gap-1 rounded-full border border-primary/30 bg-surface px-3
                text-xs font-medium text-primary transition-colors hover:bg-recessed
                focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-1"
            >
              <X size={14} aria-hidden="true" />
              {t('inbox.mirror_exit')}
            </button>
          </div>
        )}

        <div
          id="inbox-filter-panel"
          data-testid="inbox-agent-sort-row"
          className={`${filtersOpen ? 'block' : 'hidden'} max-h-[50vh] overflow-y-auto overscroll-contain rounded-2xl bg-recessed/45 p-2
            bp-lg:block bp-lg:max-h-none bp-lg:overflow-visible`}
        >
          <div className="grid grid-cols-2 gap-2">
            <div className="relative min-w-0">
              <select
                value={filter}
                onChange={(event) => changeFilter(event.target.value as InboxStatus)}
                aria-label={t('inbox.filter_label')}
                className="h-11 bp-lg:h-9 w-full cursor-pointer appearance-none rounded-full border border-default bg-surface
                  ps-3.5 pe-9 text-xs font-medium text-primary transition-colors
                  focus:border-primary/45 focus:outline-none"
              >
                {statusOptionsFor(isAdmin, isManagerRole).map((option) => <option key={option.key} value={option.key}>{t(statusLabelKey(option, isManagerRole))}</option>)}
              </select>
              <ChevronDown size={15} aria-hidden="true" className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-muted" />
            </div>

            {showBranchFilter && (
              <div className="relative min-w-0">
                <select
                  value={branchFilter}
                  onChange={(event) => setBranchFilter(event.target.value)}
                  aria-label={t('inbox.filter_all_branches')}
                  className="h-11 bp-lg:h-9 w-full cursor-pointer appearance-none rounded-full border border-default bg-surface
                    ps-3.5 pe-9 text-xs font-medium text-primary transition-colors
                    focus:border-primary/45 focus:outline-none"
                >
                  <option value="">{t('inbox.filter_all_branches')}</option>
                  {branchChoices.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
                </select>
                <ChevronDown size={15} aria-hidden="true" className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-muted" />
              </div>
            )}

            {showDepartmentFilter && (
              <div className="relative min-w-0">
                <select
                  value={deptFilter}
                  onChange={(event) => setDeptFilter(event.target.value)}
                  aria-label={t('inbox.filter_all')}
                  className="h-11 bp-lg:h-9 w-full cursor-pointer appearance-none rounded-full border border-default bg-surface
                    ps-3.5 pe-9 text-xs font-medium text-primary transition-colors
                    focus:border-primary/45 focus:outline-none"
                >
                  <option value="">{t('inbox.filter_all')}</option>
                  {departments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}
                </select>
                <ChevronDown size={15} aria-hidden="true" className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-muted" />
              </div>
            )}

            <div className={`relative min-w-0 ${!isManager && visibleFilterCount % 2 === 1 ? 'col-span-2' : ''}`}>
              <Tag
                size={14}
                aria-hidden="true"
                className="pointer-events-none absolute start-3 top-1/2 z-10 -translate-y-1/2 text-muted"
              />
              <select
                value={tagFilter}
                onChange={(event) => setTagFilter(event.target.value)}
                aria-label={t('inbox.filter_tags_label')}
                className="h-11 bp-lg:h-9 w-full cursor-pointer appearance-none rounded-full border border-default bg-surface
                  ps-8 pe-9 text-xs font-medium text-primary transition-colors
                  focus:border-primary/45 focus:outline-none"
              >
                <option value="">{t('inbox.filter_all_tags')}</option>
                {tags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}
              </select>
              <ChevronDown size={15} aria-hidden="true" className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-muted" />
            </div>

            {isManager && (
              <div className={`relative min-w-0 ${visibleFilterCount % 2 === 1 ? 'col-span-2' : ''}`}>
                <select
                  value={agentFilter}
                  onChange={(event) => selectViewedAgent(event.target.value)}
                  aria-label={t('inbox.filter_agents_label')}
                  className="h-11 bp-lg:h-9 w-full cursor-pointer appearance-none rounded-full border border-primary/25
                    bg-primary/[0.055] ps-3.5 pe-9 text-xs font-medium text-primary transition-colors
                    focus:border-primary/50 focus:outline-none"
                >
                  <option value="">{t('inbox.filter_all_agents')}</option>
                  <option value="unassigned">{t('inbox.filter_unassigned_agent')}</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <ChevronDown size={15} aria-hidden="true" className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-primary/65" />
              </div>
            )}
          </div>

          <div className="mt-2 border-t border-default/65 px-1 pt-2">
            {(filter === 'unread' || filter === 'assigned_unread') && (
              <div
                data-testid="inbox-result-summary"
                className="flex min-h-9 flex-wrap items-center gap-2"
              >
                <span
                  className="inline-flex min-h-9 max-w-full min-w-0 items-center rounded-full bg-warning/10 px-3 py-2
                    text-label-small font-medium leading-4 text-status-warning"
                  aria-live="polite"
                >
                  {t('inbox.unread_count_summary', { count: filter === 'assigned_unread' ? assignedUnreadFilterCount : unreadFilterCount })}
                </span>
              </div>
            )}
            <div
              data-testid="inbox-sort-control"
              className={`${filter === 'unread' || filter === 'assigned_unread' ? 'mt-2 ' : ''}grid h-[50px] bp-lg:h-10 w-full min-w-0 items-stretch rounded-full border border-outline-variant/80
                bg-surface p-0.5 shadow-[0_1px_4px_rgb(var(--md-sys-color-shadow)/0.04)]
                ${sortOptions.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}
            >
              {sortOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => setSort(option.value)}
                  aria-pressed={sort === option.value}
                  className={`flex h-full min-w-0 items-center justify-center rounded-full px-1.5 text-center
                    text-label-small font-medium leading-4 transition-[background-color,color,box-shadow]
                    focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-1
                    ${sort === option.value
                      ? 'bg-primary/[0.09] text-primary shadow-[0_2px_8px_rgb(var(--md-sys-color-shadow)/0.12)]'
                      : 'text-muted hover:text-primary'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        ref={listRef}
        data-testid="inbox-conversation-list"
        role="presentation"
        onScroll={onListScroll}
        onKeyDown={onListKeyDown}
        className="flex min-h-0 flex-1 flex-col divide-y divide-outline-variant/45
          overflow-y-auto bg-surface px-2 py-1.5"
      >
        {loading ? <ListSkeleton /> : error ? (
          <div data-testid="inbox-error" role="alert" className="flex h-52 flex-col items-center justify-center gap-3 px-6 text-center">
            <Spark variant="empty-state" className="text-muted" />
            <span className="text-sm text-secondary">{error}</span>
            {mirrorRefused ? (
              <button
                type="button"
                onClick={exitMirror}
                className="min-h-11 rounded-full border border-default px-4 py-2 text-xs font-medium text-primary
                  transition-colors hover:bg-recessed"
              >
                {t('inbox.filter_all_agents')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void fetchReset()}
                className="min-h-11 rounded-full border border-default px-4 py-2 text-xs font-medium text-primary
                  transition-colors hover:bg-recessed"
              >
                {t('retry')}
              </button>
            )}
          </div>
        ) : rows.length === 0 ? (
          <div data-testid="inbox-empty" className="flex h-64 flex-col items-center justify-center gap-2.5 px-6 text-center">
            <span className="flex size-12 items-center justify-center rounded-2xl bg-recessed text-muted">
              {/* sized to its 48px badge (the default empty-state mark is 64px and overflowed it) */}
              <Spark variant="empty-state" className="!size-8" />
            </span>
            <p className="mt-1 text-sm font-semibold text-primary">{emptyTitle}</p>
            <p className="max-w-[15rem] text-xs leading-5 text-muted">{t('inbox.empty_subtitle')}</p>
          </div>
        ) : (
          <>
            {rows.map((row) => (
              <InboxListRow
                key={rowKey(row)}
                row={row}
                templateCatalog={templateCatalog}
                selected={row.ticketId === selectedId || (!row.ticketId && row.phone === selectedPhone)}
                onOpen={openRow}
              />
            ))}
            {loadingMore && (
              <div
                aria-hidden="true"
                className="mx-auto my-3 h-5 w-5 animate-spin rounded-full border-2 border-default border-t-primary"
              />
            )}
          </>
        )}
      </div>
    </section>
  );

  const detailPane = (
    <section className={`${hasSelection ? 'flex' : 'hidden bp-lg:flex'} flex-col flex-1 min-w-0 min-h-0 bg-surface`}>
      <Suspense
        fallback={(
          <div
            role="status"
            className="flex flex-1 items-center justify-center text-body-medium text-on-surface-variant"
          >
            {t('loading')}
          </div>
        )}
      >
        {selectedId ? (
          <TicketDetailPane
            id={selectedId}
            customerId={selectedCustomerId}
            onBack={closeDetail}
            onStateChanged={onTicketStateChanged}
          />
        ) : selectedPhone ? (
          <ConversationDetailPane
            phone={selectedPhone}
            customerId={selectedCustomerId}
            blocked={selectedBlocked}
            onBack={closeDetail}
            onOpenTicket={openTicketFromConversation}
            onStateChanged={onConversationStateChanged}
          />
        ) : <EmptyDetail />}
      </Suspense>
    </section>
  );

  return (
    <>
      {showOutbound && <OutboundModal branches={branches} initialMode="template" lockToTemplate onClose={() => setShowOutbound(false)} onSuccess={(ticketId) => { setShowOutbound(false); navigate(`/tickets/${ticketId}?status=${filter}`); }} />}
      {/* Fills the app shell (Layout: `.app-viewport-h`, a vh → dvh → visual-viewport fallback chain) below the
          header and any banner — no hand-subtracted header height. min-h-0 lets the panes scroll inside. */}
      <div
        className="flex min-h-0 flex-1 overflow-hidden rounded-[1.25rem] border border-default/80
          bg-surface shadow-[0_18px_60px_rgb(20_41_38/0.055)]"
      >
        {listPane}
        {detailPane}
      </div>
    </>
  );
}
